/**
 * Professional Planning PDF Generator
 * Generates customer-facing project plan documents
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

// Color palette for professional look
const COLORS = {
  primary: [59, 130, 246],      // Blue
  secondary: [139, 92, 246],    // Purple
  accent: [16, 185, 129],       // Green
  dark: [17, 24, 39],           // Dark gray
  medium: [107, 114, 128],      // Medium gray
  light: [243, 244, 246],       // Light gray
  white: [255, 255, 255],
  success: [34, 197, 94],
  warning: [245, 158, 11],
  danger: [239, 68, 68],
};

// Status colors
const STATUS_COLORS = {
  'Planned': COLORS.primary,
  'In Progress': COLORS.warning,
  'Completed': COLORS.success,
  'On Hold': COLORS.medium,
  'Cancelled': COLORS.danger,
};

// Priority colors
const PRIORITY_COLORS = {
  'Low': COLORS.medium,
  'Medium': COLORS.primary,
  'High': COLORS.warning,
  'Critical': COLORS.danger,
};

/**
 * Format date for display
 */
function formatDate(date) {
  if (!date) return 'TBD';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
}

/**
 * Format short date
 */
function formatShortDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
}

/**
 * Calculate duration in days
 */
function calculateDuration(startDate, endDate) {
  if (!startDate || !endDate) return 'TBD';
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
}

/**
 * Add page header
 */
function addPageHeader(doc, companyName, pageTitle) {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header background
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 25, 'F');
  
  // Gradient effect
  doc.setFillColor(...COLORS.secondary);
  doc.rect(pageWidth - 80, 0, 80, 25, 'F');
  
  // Company name
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(companyName || 'Project Plan', 15, 16);
  
  // Page title
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(pageTitle, pageWidth - 15, 16, { align: 'right' });
}

/**
 * Add page footer
 */
function addPageFooter(doc, pageNumber, totalPages) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Footer line
  doc.setDrawColor(...COLORS.light);
  doc.setLineWidth(0.5);
  doc.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);
  
  // Page number
  doc.setTextColor(...COLORS.medium);
  doc.setFontSize(9);
  doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  
  // Generated date
  doc.text(`Generated: ${formatDate(new Date())}`, 15, pageHeight - 8);
  
  // Confidential notice
  doc.text('Confidential', pageWidth - 15, pageHeight - 8, { align: 'right' });
}

/**
 * Generate Cover Page
 */
function generateCoverPage(doc, companyName, tasks, dateRange) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Background gradient effect
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, pageHeight * 0.45, 'F');
  
  // Decorative elements
  doc.setFillColor(100, 150, 255);
  doc.circle(pageWidth - 30, 50, 80, 'F');
  doc.setFillColor(80, 120, 230);
  doc.circle(-20, pageHeight * 0.35, 60, 'F');
  
  // Company Logo placeholder / Title
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('PROJECT PLAN', pageWidth / 2, 40, { align: 'center' });
  
  // Main title
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  const title = companyName || 'Onboarding Project';
  doc.text(title, pageWidth / 2, 75, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Implementation & Deployment Plan', pageWidth / 2, 95, { align: 'center' });
  
  // Date range
  doc.setFontSize(12);
  doc.text(`${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`, pageWidth / 2, 115, { align: 'center' });
  
  // Stats boxes
  const boxY = pageHeight * 0.52;
  const boxWidth = 45;
  const boxHeight = 55;
  const boxSpacing = 10;
  const totalBoxWidth = (boxWidth * 4) + (boxSpacing * 3);
  const startX = (pageWidth - totalBoxWidth) / 2;
  
  const stats = [
    { label: 'Total Tasks', value: tasks.length, color: COLORS.primary },
    { label: 'Planned', value: tasks.filter(t => t.status === 'Planned').length, color: COLORS.secondary },
    { label: 'In Progress', value: tasks.filter(t => t.status === 'In Progress').length, color: COLORS.warning },
    { label: 'Completed', value: tasks.filter(t => t.status === 'Completed').length, color: COLORS.success },
  ];
  
  stats.forEach((stat, index) => {
    const x = startX + (boxWidth + boxSpacing) * index;
    
    // Box shadow
    doc.setFillColor(220, 220, 220);
    doc.roundedRect(x + 2, boxY + 2, boxWidth, boxHeight, 4, 4, 'F');
    
    // Box background
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(x, boxY, boxWidth, boxHeight, 4, 4, 'F');
    
    // Value
    doc.setTextColor(...stat.color);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(String(stat.value), x + boxWidth / 2, boxY + 25, { align: 'center' });
    
    // Label
    doc.setTextColor(...COLORS.medium);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + boxWidth / 2, boxY + 42, { align: 'center' });
  });
  
  // Document info section
  const infoY = pageHeight * 0.75;
  const infoHeight = 55;
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(25, infoY, pageWidth - 50, infoHeight, 5, 5, 'F');
  
  // Document Information Title
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Document Information', pageWidth / 2, infoY + 12, { align: 'center' });
  
  // Draw a subtle line under title
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(45, infoY + 17, pageWidth - 45, infoY + 17);
  
  // Info grid - 2 columns, 2 rows
  doc.setFontSize(9);
  const col1X = 45;
  const col2X = pageWidth / 2 + 10;
  const row1Y = infoY + 28;
  const row2Y = infoY + 42;
  
  // Row 1, Col 1 - Prepared For
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.medium);
  doc.text('Prepared For:', col1X, row1Y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text(companyName || 'Client', col1X + 28, row1Y);
  
  // Row 1, Col 2 - Document Date
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.medium);
  doc.text('Document Date:', col2X, row1Y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text(formatDate(new Date()), col2X + 32, row1Y);
  
  // Row 2, Col 1 - Prepared By
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.medium);
  doc.text('Prepared By:', col1X, row2Y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text('MIBOT Onboarding Team', col1X + 28, row2Y);
  
  // Row 2, Col 2 - Version
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.medium);
  doc.text('Version:', col2X, row2Y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text('1.0', col2X + 32, row2Y);
  
  // Footer
  doc.setTextColor(...COLORS.medium);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Powered by MIBOT Onboarding System', pageWidth / 2, pageHeight - 15, { align: 'center' });
}

/**
 * Generate Executive Summary Page
 */
function generateExecutiveSummary(doc, companyName, tasks, dateRange) {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  addPageHeader(doc, companyName, 'Executive Summary');
  
  let y = 40;
  
  // Section title
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Summary', 15, y);
  y += 15;
  
  // Introduction
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.medium);
  
  const introText = `This document outlines the comprehensive project plan for ${companyName || 'the client'}'s onboarding implementation. The plan covers all scheduled tasks, milestones, and deliverables from ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}.`;
  
  const splitIntro = doc.splitTextToSize(introText, pageWidth - 30);
  doc.text(splitIntro, 15, y);
  y += splitIntro.length * 5 + 10;
  
  // Project Overview Box
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(15, y, pageWidth - 30, 45, 3, 3, 'F');
  
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Project Overview', 25, y + 12);
  
  // Calculate project stats
  const totalDays = Math.ceil((new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24));
  const completedTasks = tasks.filter(t => t.status === 'Completed').length;
  const progress = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const overviewItems = [
    `• Project Duration: ${totalDays} days`,
    `• Total Tasks: ${tasks.length}`,
    `• Current Progress: ${progress}% complete`,
    `• Team Members Assigned: ${new Set(tasks.flatMap(t => t.assignedUsers?.map(u => u.id) || [])).size}`,
  ];
  
  overviewItems.forEach((item, index) => {
    doc.text(item, 25, y + 24 + (index * 6));
  });
  
  y += 55;
  
  // Task Breakdown by Status
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Task Status Breakdown', 15, y);
  y += 10;
  
  const statusCounts = {
    'Planned': tasks.filter(t => t.status === 'Planned').length,
    'In Progress': tasks.filter(t => t.status === 'In Progress').length,
    'Completed': tasks.filter(t => t.status === 'Completed').length,
    'On Hold': tasks.filter(t => t.status === 'On Hold').length,
    'Cancelled': tasks.filter(t => t.status === 'Cancelled').length,
  };
  
  // Status bars
  Object.entries(statusCounts).forEach(([status, count]) => {
    if (count === 0 && status !== 'Planned' && status !== 'In Progress' && status !== 'Completed') return;
    
    const barWidth = tasks.length > 0 ? (count / tasks.length) * (pageWidth - 100) : 0;
    const color = STATUS_COLORS[status] || COLORS.medium;
    
    // Status label
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(status, 15, y + 4);
    
    // Background bar
    doc.setFillColor(...COLORS.light);
    doc.roundedRect(70, y - 2, pageWidth - 100, 8, 2, 2, 'F');
    
    // Progress bar
    if (barWidth > 0) {
      doc.setFillColor(...color);
      doc.roundedRect(70, y - 2, Math.max(barWidth, 4), 8, 2, 2, 'F');
    }
    
    // Count
    doc.setTextColor(...COLORS.dark);
    doc.text(`${count}`, pageWidth - 20, y + 4, { align: 'right' });
    
    y += 12;
  });
  
  y += 10;
  
  // Priority Distribution
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Priority Distribution', 15, y);
  y += 10;
  
  const priorityCounts = {
    'Critical': tasks.filter(t => t.priority === 'Critical').length,
    'High': tasks.filter(t => t.priority === 'High').length,
    'Medium': tasks.filter(t => t.priority === 'Medium').length,
    'Low': tasks.filter(t => t.priority === 'Low').length,
  };
  
  Object.entries(priorityCounts).forEach(([priority, count]) => {
    if (count === 0) return;
    
    const color = PRIORITY_COLORS[priority] || COLORS.medium;
    
    // Priority badge
    doc.setFillColor(...color);
    doc.roundedRect(15, y - 4, 50, 10, 2, 2, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(8);
    doc.text(priority, 40, y + 2, { align: 'center' });
    
    // Count
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(10);
    doc.text(`${count} task${count !== 1 ? 's' : ''}`, 75, y + 2);
    
    y += 14;
  });
  
  return y;
}

/**
 * Generate Task Table Pages
 */
function generateTaskTable(doc, companyName, tasks) {
  doc.addPage();
  addPageHeader(doc, companyName, 'Detailed Task List');
  
  // Sort tasks by start date
  const sortedTasks = [...tasks].sort((a, b) => new Date(a.start) - new Date(b.start));
  
  // Section title
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Detailed Task List', 15, 40);
  
  // Prepare table data
  const tableData = sortedTasks.map((task, index) => [
    index + 1,
    task.title || task.name || 'Untitled',
    task.type || '-',
    formatDate(task.start),
    formatDate(task.end),
    calculateDuration(task.start, task.end),
    task.status || 'Planned',
    task.priority || 'Medium',
    task.assignedUsers?.map(u => u.name).join(', ') || 'Unassigned',
  ]);
  
  // Generate table using autoTable function
  autoTable(doc, {
    startY: 50,
    head: [['#', 'Task Name', 'Type', 'Start Date', 'End Date', 'Duration', 'Status', 'Priority', 'Assigned To']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 3,
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2,
    },
    alternateRowStyles: {
      fillColor: COLORS.light,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 35 },
      2: { cellWidth: 20 },
      3: { cellWidth: 22 },
      4: { cellWidth: 22 },
      5: { cellWidth: 18 },
      6: { cellWidth: 20 },
      7: { cellWidth: 18 },
      8: { cellWidth: 30 },
    },
    didDrawCell: (data) => {
      // Color-code status cells
      if (data.column.index === 6 && data.cell.section === 'body') {
        const status = data.cell.raw;
        const color = STATUS_COLORS[status];
        if (color) {
          doc.setFillColor(...color);
          doc.roundedRect(
            data.cell.x + 1,
            data.cell.y + 1,
            data.cell.width - 2,
            data.cell.height - 2,
            1, 1, 'F'
          );
          doc.setTextColor(...COLORS.white);
          doc.setFontSize(7);
          doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, { align: 'center' });
        }
      }
      // Color-code priority cells
      if (data.column.index === 7 && data.cell.section === 'body') {
        const priority = data.cell.raw;
        const color = PRIORITY_COLORS[priority];
        if (color) {
          doc.setFillColor(...color);
          doc.roundedRect(
            data.cell.x + 1,
            data.cell.y + 1,
            data.cell.width - 2,
            data.cell.height - 2,
            1, 1, 'F'
          );
          doc.setTextColor(...COLORS.white);
          doc.setFontSize(7);
          doc.text(priority, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, { align: 'center' });
        }
      }
    },
    margin: { left: 10, right: 10 },
  });
}

/**
 * Generate Gantt Chart Page (Landscape)
 */
async function generateGanttPage(doc, companyName, tasks, dateRange, ganttElement) {
  // Add landscape page
  doc.addPage('a4', 'landscape');
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 20, 'F');
  doc.setFillColor(...COLORS.secondary);
  doc.rect(pageWidth - 60, 0, 60, 20, 'F');
  
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`${companyName || 'Project'} - Project Timeline`, 15, 13);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Gantt Chart', pageWidth - 15, 13, { align: 'right' });
  
  // Always use the improved Gantt chart generator
  generateImprovedGantt(doc, tasks, dateRange, pageWidth, pageHeight);
}

/**
 * Generate improved Gantt visualization
 */
function generateImprovedGantt(doc, tasks, dateRange, pageWidth, pageHeight) {
  const sortedTasks = [...tasks].sort((a, b) => new Date(a.start) - new Date(b.start));
  
  if (sortedTasks.length === 0) {
    doc.setTextColor(...COLORS.medium);
    doc.setFontSize(14);
    doc.text('No tasks scheduled', pageWidth / 2, pageHeight / 2, { align: 'center' });
    return;
  }
  
  // Chart dimensions
  const chartLeft = 75;
  const chartRight = pageWidth - 15;
  const chartTop = 55;
  const chartWidth = chartRight - chartLeft;
  const legendHeight = 25;
  const availableHeight = pageHeight - chartTop - legendHeight - 15;
  const maxRowHeight = 14;
  const minRowHeight = 8;
  const rowHeight = Math.max(minRowHeight, Math.min(maxRowHeight, availableHeight / sortedTasks.length));
  const chartHeight = sortedTasks.length * rowHeight;
  
  // Calculate date range
  const minDate = new Date(dateRange.start);
  const maxDate = new Date(dateRange.end);
  const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) || 1;
  
  // Date range label
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Timeline: ${formatDate(minDate)} - ${formatDate(maxDate)} (${totalDays} days)`, 15, 32);
  
  // Draw timeline header background
  doc.setFillColor(...COLORS.primary);
  doc.rect(chartLeft, chartTop - 20, chartWidth, 18, 'F');
  
  // Draw week markers in header
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  
  // Calculate week intervals
  const weekInterval = Math.max(7, Math.ceil(totalDays / 10)); // Show ~10 markers max
  let currentDate = new Date(minDate);
  
  // Draw date markers
  while (currentDate <= maxDate) {
    const dayOffset = Math.ceil((currentDate - minDate) / (1000 * 60 * 60 * 24));
    const x = chartLeft + (dayOffset / totalDays) * chartWidth;
    
    if (x >= chartLeft && x <= chartRight - 15) {
      // Date label in header
      const dateLabel = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      doc.text(dateLabel, x + 2, chartTop - 8);
      
      // Vertical grid line
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.line(x, chartTop, x, chartTop + chartHeight);
    }
    
    currentDate.setDate(currentDate.getDate() + weekInterval);
  }
  
  // Draw task rows
  sortedTasks.forEach((task, index) => {
    const y = chartTop + index * rowHeight;
    const isEven = index % 2 === 0;
    
    // Row background (alternating)
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
    doc.rect(0, y, pageWidth, rowHeight, 'F');
    
    // Task name (left side)
    const taskName = (task.title || task.name || 'Task').substring(0, 12);
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(taskName, 5, y + rowHeight / 2 + 2);
    
    // Calculate bar position
    const taskStart = new Date(task.start);
    const taskEnd = new Date(task.end);
    
    // Clamp dates to the visible range
    const clampedStart = new Date(Math.max(taskStart, minDate));
    const clampedEnd = new Date(Math.min(taskEnd, maxDate));
    
    const startOffset = (clampedStart - minDate) / (1000 * 60 * 60 * 24);
    const endOffset = (clampedEnd - minDate) / (1000 * 60 * 60 * 24);
    
    const barStart = chartLeft + (startOffset / totalDays) * chartWidth;
    const barWidth = Math.max(((endOffset - startOffset) / totalDays) * chartWidth, 4);
    const barHeight = rowHeight - 4;
    const barY = y + 2;
    
    // Get color based on status
    const statusColor = STATUS_COLORS[task.status] || COLORS.primary;
    
    // Draw task bar with shadow effect
    doc.setFillColor(200, 200, 200);
    doc.roundedRect(barStart + 1, barY + 1, barWidth, barHeight, 2, 2, 'F');
    
    // Task bar
    doc.setFillColor(...statusColor);
    doc.roundedRect(barStart, barY, barWidth, barHeight, 2, 2, 'F');
    
    // Task dates on bar (if bar is wide enough)
    if (barWidth > 50) {
      doc.setTextColor(...COLORS.white);
      doc.setFontSize(5);
      doc.setFont('helvetica', 'normal');
      const dateText = `${formatShortDate(task.start)} - ${formatShortDate(task.end)}`;
      doc.text(dateText, barStart + barWidth / 2, barY + barHeight / 2 + 1.5, { align: 'center' });
    }
    
    // Draw row separator line
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(0, y + rowHeight, pageWidth, y + rowHeight);
  });
  
  // Draw chart border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.rect(chartLeft, chartTop, chartWidth, chartHeight);
  
  // Legend at bottom
  const legendY = chartTop + chartHeight + 10;
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(15, legendY, pageWidth - 30, legendHeight - 5, 3, 3, 'F');
  
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Legend:', 25, legendY + 12);
  
  let legendX = 55;
  const legendItems = [
    { status: 'Planned', color: COLORS.primary },
    { status: 'In Progress', color: COLORS.warning },
    { status: 'Completed', color: COLORS.success },
    { status: 'On Hold', color: COLORS.medium },
    { status: 'Cancelled', color: COLORS.danger },
  ];
  
  legendItems.forEach(({ status, color }) => {
    // Color dot
    doc.setFillColor(...color);
    doc.circle(legendX, legendY + 10, 3, 'F');
    
    // Label
    doc.setTextColor(...COLORS.dark);
    doc.setFont('helvetica', 'normal');
    doc.text(status, legendX + 6, legendY + 12);
    
    legendX += 45;
  });
}

/**
 * Helper: Convert hex to RGB
 */
function hexToRgb(hex) {
  if (!hex) return COLORS.primary;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : COLORS.primary;
}

/**
 * Main PDF Generation Function
 */
export async function generatePlanningPdf(companyName, tasks, dateRange, ganttElement = null) {
  // Create new PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });
  
  // Page 1: Cover
  generateCoverPage(doc, companyName, tasks, dateRange);
  
  // Page 2: Executive Summary
  doc.addPage();
  generateExecutiveSummary(doc, companyName, tasks, dateRange);
  
  // Page 3: Task Table
  generateTaskTable(doc, companyName, tasks);
  
  // Page 4: Gantt Chart (Landscape)
  await generateGanttPage(doc, companyName, tasks, dateRange, ganttElement);
  
  // Add page numbers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Skip cover page for footer
    if (i > 1) {
      addPageFooter(doc, i - 1, totalPages - 1);
    }
  }
  
  // Generate filename
  const safeCompanyName = (companyName || 'Project').replace(/[^a-zA-Z0-9]/g, '_');
  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `${safeCompanyName}_Project_Plan_${dateStr}.pdf`;
  
  // Save the PDF
  doc.save(filename);
  
  return filename;
}

export default generatePlanningPdf;
